import { type MouseEventHandler, type ReactNode } from 'react';
import styles from './index.module.scss';

type Props = {
  variant: 'primary' | 'secondary';
  href: string;
  children: ReactNode;
  icon?: ReactNode;
  preLabel?: string;
  microcopy?: string;
  urgency?: string;
  target?: string;
  rel?: string;
  dataTrackingId?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  colorScheme?: 'orange';
};

export const CTAButton = ({
  variant,
  href,
  children,
  icon,
  preLabel,
  microcopy,
  urgency,
  target,
  rel,
  dataTrackingId,
  onClick,
  colorScheme,
}: Props) => {
  const relTokens = (rel ?? '').split(' ').filter(Boolean);

  if (target === '_blank') {
    if (!relTokens.includes('noopener')) {
      relTokens.push('noopener');
    }
    if (!relTokens.includes('noreferrer')) {
      relTokens.push('noreferrer');
    }
  }

  const safeRel = relTokens.length > 0 ? relTokens.join(' ') : undefined;
  const hasWrapper = Boolean(preLabel) || Boolean(microcopy) || Boolean(urgency) || Boolean(colorScheme);

  const link = (
    <a
      href={href}
      target={target}
      rel={safeRel}
      data-tracking-id={dataTrackingId}
      className={`${styles.base} ${variant === 'primary' ? styles.primary : styles.secondary}`}
      onClick={onClick}
    >
      {icon ? <span className={styles.icon}>{icon}</span> : null}
      <span className={styles.text}>{children}</span>
    </a>
  );

  if (!hasWrapper) {
    return link;
  }

  const wrapperClassName = [styles.wrapper, colorScheme === 'orange' ? styles['scheme-orange'] : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClassName}>
      {preLabel ? <span className={styles['pre-label']}>{preLabel}</span> : null}
      {link}
      {microcopy ? <p className={styles.microcopy}>{microcopy}</p> : null}
      {urgency ? <p className={styles.urgency}>{urgency}</p> : null}
    </div>
  );
};
